/**
 * UserMenu - User menu dropdown component with icon
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProviderWrapper';
import { useClickOutside } from '../hooks/useClickOutside';
import { UserMenuItems } from './UserMenu/UserMenuItems';
import { Button } from './design-system';

export const UserMenu: React.FC = () => {
  const { signOut, session, isAuthenticated, isLoading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number | 'auto'; right: number | 'auto' } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await signOut();
    // Navigate to landing page after sign out
    navigate('/');
  };

  const handleClose = () => setIsOpen(false);

  // Calculate menu position to prevent off-screen display
  useEffect(() => {
    const calculatePosition = () => {
      if (isOpen && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const menuWidth = 256; // w-64 = 16rem = 256px
        const menuHeight = 300; // Approximate height
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        // Calculate position
        let top = buttonRect.bottom + window.scrollY;
        let left: number | 'auto' = buttonRect.left + window.scrollX;
        let right: number | 'auto' = 'auto';
        
        // Adjust for right boundary
        if (left + menuWidth > screenWidth) {
          left = 'auto' as const;
          right = screenWidth - buttonRect.right + window.scrollX;
        }
        
        // Adjust for bottom boundary
        if (top + menuHeight > screenHeight + window.scrollY) {
          top = buttonRect.top + window.scrollY - menuHeight - 8;
        }
        
        setMenuPosition({ top, left, right });
      }
    };

    if (isOpen) {
      calculatePosition();
      // Recalculate on scroll/resize
      window.addEventListener('scroll', calculatePosition);
      window.addEventListener('resize', calculatePosition);
      
      return () => {
        window.removeEventListener('scroll', calculatePosition);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isOpen, location.pathname]);

  // Use custom hook for click outside detection
  useClickOutside(menuRef, handleClose, isOpen);

  // Get user initials for avatar
  const userInitials = session?.user?.email
    ? session.user.email.substring(0, 2).toUpperCase()
    : 'U';

  // Menu element to be rendered via portal
  const menuElement = menuPosition && isOpen ? (
    <div 
      ref={menuRef}
      className="fixed w-64 bg-surface-primary rounded-lg shadow-lg border border-divider py-2 z-[100]"
      style={{
        top: menuPosition.top,
        left: menuPosition.left !== 'auto' ? menuPosition.left : 'auto',
        right: menuPosition.right !== 'auto' ? menuPosition.right : 'auto',
      }}
    >
      {isLoading ? (
        <div className="px-4 py-3 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary mx-auto"></div>
        </div>
      ) : isAuthenticated && session?.user ? (
        <>
          {/* User Info */}
          <div className="px-4 py-3 border-b border-divider">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center">
                <span className="text-brand-primary font-semibold text-sm">
                  {userInitials}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-primary truncate">
                  {session.user.email}
                </p>
                <p className="text-xs text-secondary">
                  Signed in
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <UserMenuItems onClose={handleClose} onLogout={handleLogout} />
        </>
      ) : (
        <>
          {/* Not signed in */}
          <div className="px-4 py-3">
            <p className="text-sm text-secondary mb-3">Sign in to access your profile</p>
            <Link 
              to="/login" 
              className="block w-full text-center px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
              onClick={handleClose}
            >
              Sign In
            </Link>
            <Link 
              to="/register" 
              className="block w-full text-center px-4 py-2 mt-2 border border-brand-primary text-brand-primary rounded-lg hover:bg-brand-primary/10 transition-colors"
              onClick={handleClose}
            >
              Create Account
            </Link>
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="relative" ref={buttonRef}>
      {/* User Icon Button - Always visible */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        size="md"
        className="rounded-full bg-surface-secondary hover:bg-surface-tertiary"
        aria-label="User menu"
      >
        <svg 
          className="w-6 h-6 text-secondary" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
          />
        </svg>
      </Button>

      {/* Render menu via portal to avoid clipping */}
      {menuElement && createPortal(menuElement, document.body)}
    </div>
  );
};